'use client';

import React from 'react';
import styled, { type DefaultTheme } from 'styled-components';
import { darkColors } from '@liftango/ui-tokens';

export type ChipTone = 'default' | 'success' | 'warn' | 'error' | 'info';

export interface MetaChipProps {
  icon?: React.ReactNode;
  tone?: ChipTone;
  title?: string;
  /**
   * Render against a dark surface (the Lifty Bold variant). Swaps the
   * default chip background to dark-panel and the ink to dark-mode neutrals
   * while preserving tone colors. Coloured tones become slightly muted.
   */
  dark?: boolean;
  children?: React.ReactNode;
  className?: string;
}

const toneStyles = (tone: ChipTone, theme: DefaultTheme, dark: boolean) => {
  if (tone !== 'default') {
    const semanticKey =
      tone === 'success'
        ? 'success'
        : tone === 'warn'
          ? 'warn'
          : tone === 'error'
            ? 'error'
            : 'info';
    const sem = theme.colors.semantic[semanticKey];
    const borderRgba: Record<typeof semanticKey, string> = {
      success: 'rgba(46,125,50,0.28)',
      warn: 'rgba(200,140,0,0.28)',
      error: 'rgba(180,50,40,0.28)',
      info: 'rgba(63,94,146,0.28)',
    };
    return dark
      ? {
          bg: 'rgba(255,255,255,0.04)',
          border: borderRgba[semanticKey],
          color: sem.color,
        }
      : {
          bg: sem.soft,
          border: borderRgba[semanticKey],
          color: sem.color,
        };
  }
  return dark
    ? {
        bg: darkColors.neutral.panel,
        border: darkColors.neutral.line,
        color: darkColors.neutral.ink,
      }
    : {
        bg: theme.colors.neutral.panel,
        border: theme.colors.neutral.line,
        color: theme.colors.neutral.ink,
      };
};

const Pill = styled.span<{ $tone: ChipTone; $dark: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: ${(p) => p.theme.radius.pill};
  font-size: ${(p) => p.theme.fontSize.sm};
  font-family: ${(p) => p.theme.fontFamily.ui};
  line-height: 1.2;
  white-space: nowrap;
  background: ${(p) => toneStyles(p.$tone, p.theme, p.$dark).bg};
  border: 1px solid ${(p) => toneStyles(p.$tone, p.theme, p.$dark).border};
  color: ${(p) => toneStyles(p.$tone, p.theme, p.$dark).color};
`;

const IconSlot = styled.span`
  display: inline-flex;
  color: inherit;
`;

export function MetaChip({
  icon,
  tone = 'default',
  title,
  dark = false,
  children,
  className,
}: MetaChipProps) {
  return (
    <Pill $tone={tone} $dark={dark} title={title} className={className}>
      {icon && <IconSlot>{icon}</IconSlot>}
      {children != null && <span>{children}</span>}
    </Pill>
  );
}
